import { __decorate } from "tslib";
// Core
import { Component, Prop, Mixins } from 'vue-property-decorator';
import { Mutation } from 'vuex-class';
import { ROUTE } from '@/helpers/constants/Store';
// Extends
import AlertMixin from '@/helpers/vue_mixins/AlertMixin';
import CleverTapEventsMixin from '@/helpers/vue_mixins/v3/CleverTapEventsMixin';
import { validationMixin } from 'vuelidate';
import FormValidationsMixin from '@/helpers/vue_mixins/v2/FormValidationsMixin';
import ViewportSizeMixin from '@/helpers/mixins/ViewportSizeMixin';
import CategoryTreeMixin from '@/helpers/vue_mixins/v3/CategoryTreeMixin';
import PromptUpdaterMixin from '@/helpers/vue_mixins/v3/PromptUpdaterMixin';
// Children
import ActionButton from '@/components/action_button/Index.vue';
import BaseOverlayLoader from '@/components/v2/BaseOverlayLoader';
import BaseText from '@/components/BaseText.vue';
import ExplainerAdditionalDetails from '@/components/v3/prompts/ExplainerAdditionalDetails.vue';
import ExplainerStoreDetails from '@/components/v3/prompts/ExplainerStoreDetails.vue';
import ExplainerVoucher from '@/components/v3/prompts/ExplainerVoucher.vue';
import PageTitle from '@/components/v2/PageTitle.vue';
import SwitchTransition from '@/components/page_transition/Switch.vue';
// Models / Helpers
// Queries / Mutations
import StoreLaunchQuery from '@/plugins/graphql/queries/v3/fetch-generated-store-launch.gql';
import GenerateStoreLaunch from '@/plugins/graphql/mutations/v3/prompts/generate-store-launch.gql';
let StoreLaunchExplainer = class StoreLaunchExplainer extends Mixins(Mixins(AlertMixin, CleverTapEventsMixin, FormValidationsMixin, validationMixin, ViewportSizeMixin), CategoryTreeMixin, PromptUpdaterMixin) {
    constructor() {
        super(...arguments);
        // Refs
        this.storeDetails = null;
        this.voucher = null;
        this.additionalDetails = null;
        // Data
        this.prompt = null;
        // States
        this.hasPromptInStore = true;
        this.formDataIsLoaded = [false, false, false];
        this.isLoadingFormData = true;
        this.generationInProgress = false;
    }
    get storedPrompt() {
        if (this.$store.state.formData.prompt) {
            return this.$store.state.formData.prompt;
        }
        if (Object.keys(this.$store.state.storeLaunchPrompt).length > 0) {
            return this.$store.state.storeLaunchPrompt;
        }
        return null;
    }
    get retrievePromptAnswer() {
        const msg = this.prompt?.jsonCompletion?.answer || this.storedPrompt?.jsonCompletion?.answer;
        return msg?.replace(/\n/g, '<br>');
    }
    get retrievePromptReason() {
        return this.prompt?.jsonCompletion?.reason || this.storedPrompt?.jsonCompletion?.reason;
    }
    get promptUUID() {
        return this.prompt?.uuid || this.storedPrompt?.uuid;
    }
    finishLoading(index) {
        this.formDataIsLoaded[index] = true;
        if (this.isLoadingFormData && this.formDataIsLoaded.every((el) => el)) {
            this.isLoadingFormData = false;
            // Regenerate only if the route was reached via button
            // - not via redirection
            // - not via refreshing the page (from = null)
            if (!this.$store.getters['route/hasSavedRedirection'] &&
                this.$store.getters['route/from'] !== null) {
                this.regeneratePrompt();
            }
        }
    }
    isInvalidForms() {
        this.storeDetails.$v.$touch();
        this.additionalDetails.$v.$touch();
        return this.storeDetails.$v.$invalid || this.additionalDetails.$v.$invalid;
    }
    async acceptPrompt() {
        await this.updateAction(this.promptUUID, 'accept');
        this.genAIAction('Store Launch', this.$store.state.appName, 'ACCEPT');
        if (this.prompt) {
            this.$store.commit('setStoreLaunchPrompt', this.prompt);
        }
        this.$router.push({ name: 'ecommerce-overview' });
    }
    async regeneratePrompt() {
        if (this.isInvalidForms()) {
            return;
        }
        await this.updateAction(this.promptUUID, 'regenerate');
        this.genAIAction('Store Launch', this.$store.state.appName, 'REGENERATE');
        await this.generate();
    }
    ignorePrompt() {
        this.genAIAction('Store Launch', this.$store.state.appName, 'IGNORE');
        this.$router.push({ name: 'ecommerce-overview' });
    }
    generate() {
        if (this.isInvalidForms()) {
            return;
        }
        this.generationInProgress = true;
        /* eslint-disable no-underscore-dangle */
        const storeDetailsData = this.storeDetails._data;
        const voucherData = this.voucher._data;
        const additionalDetailsData = this.additionalDetails._data;
        /* eslint-enable no-underscore-dangle */
        return this.$apollo
            .mutate({
            mutation: GenerateStoreLaunch,
            variables: {
                input: {
                    appUuid: this.$store.state.appUUID,
                    promoCodeUuid: voucherData.selectedPromoCode?.uuid,
                    posts: additionalDetailsData.form.tone.map((post) => {
                        return {
                            id: post.id,
                            permalinkUrl: post.permalink_url,
                            message: post.message,
                            fullPicture: post.full_picture,
                            createdTime: post.created_time,
                        };
                    }),
                    primaryCategories: this.extractPrimaryCategories(storeDetailsData.form.categoryTree),
                    secondaryCategories: this.extractSecondaryCategories(storeDetailsData.form.categoryTree),
                    businessDescription: storeDetailsData.form.businessDescription,
                    promptContext: {
                        targetAudience: additionalDetailsData.form.targetAudience,
                        keywords: additionalDetailsData.form.keywords,
                    },
                },
            },
        })
            .then((result) => {
            this.prompt = result.data.generateStoreLaunch.base;
        })
            .catch((e) => {
            this.errorNotification(e.message);
        })
            .finally(() => {
            this.generationInProgress = false;
        });
    }
    created() {
        this.hasPromptInStore = Object.keys(this.$store.state.storeLaunchPrompt).length > 0;
    }
    mounted() {
        this.storeDetails = this.$refs.storeDetails;
        this.voucher = this.$refs.voucher;
        this.additionalDetails = this.$refs.additionalDetails;
        if (this.promptUUID) {
            this.finishLoading(0);
        }
        this.resetRouteRedirection();
    }
};
__decorate([
    Prop({ type: Object, default: null })
], StoreLaunchExplainer.prototype, "value", void 0);
__decorate([
    Mutation('resetRouteRedirection', { namespace: ROUTE })
], StoreLaunchExplainer.prototype, "resetRouteRedirection", void 0);
StoreLaunchExplainer = __decorate([
    Component({
        components: {
            ActionButton,
            BaseOverlayLoader,
            BaseText,
            ExplainerAdditionalDetails,
            ExplainerStoreDetails,
            ExplainerVoucher,
            PageTitle,
            SwitchTransition,
        },
        apollo: {
            fetchPrompt: {
                query: StoreLaunchQuery,
                variables() {
                    return {
                        appUuid: this.$store.state.appUUID,
                    };
                },
                update({ fetchGeneratedStoreLaunch }) {
                    if (fetchGeneratedStoreLaunch) {
                        this.prompt = fetchGeneratedStoreLaunch;
                        this.$store.commit('setStoreLaunchPrompt', fetchGeneratedStoreLaunch);
                        this.finishLoading(0);
                    }
                    // } else {
                    //   this.$router.push({ name: 'ecommerce-overview' });
                    // }
                },
                skip() {
                    return !this.$store.state.appUUID || this.hasPromptInStore;
                },
            },
        },
        beforeRouteLeave(to, from, next) {
            if (to.name === 'ecommerce-promos-create') {
                /* eslint-disable no-underscore-dangle */
                this.$store.commit('setFormData', {
                    prompt: this.prompt,
                    storeDetails: this.storeDetails._data.form,
                    voucher: this.voucher._data.selectedPromoCode,
                    additionalDetails: this.additionalDetails._data.form,
                });
                /* eslint-enable no-underscore-dangle */
            }
            else {
                this.$store.commit('resetFormData');
            }
            next();
        },
    })
], StoreLaunchExplainer);
export default StoreLaunchExplainer;
